"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _Dimensions = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Dimensions"));
var _TouchableWithoutFeedback = _interopRequireDefault(require("react-native-web/dist/cjs/exports/TouchableWithoutFeedback"));
var _Modal = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Modal"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _SafeAreaView = _interopRequireDefault(require("react-native-web/dist/cjs/exports/SafeAreaView"));
var _ScrollView = _interopRequireDefault(require("react-native-web/dist/cjs/exports/ScrollView"));
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _utils = require("../utils");
var _ViewportProvider = require("../ViewportProvider");
var _ThemeProvider = require("../ThemeProvider");
var _Typography = _interopRequireDefault(require("../Typography"));
var _Icon = _interopRequireDefault(require("../Icon"));
var _OrderedList = _interopRequireDefault(require("../OrderedList"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.htmlAttrs, _utils.viewProps]);
const selectFootnoteStyle = _ref => {
  let {
    screenHeight,
    footnoteBackground,
    isVisible,
    footnoteBorderTopSizeMd,
    footnoteBorderColorMd
  } = _ref;
  return {
    left: 0,
    backgroundColor: footnoteBackground,
    display: isVisible ? 'flex' : 'none',
    height: screenHeight,
    borderTopColor: footnoteBorderColorMd,
    borderTopWidth: footnoteBorderTopSizeMd
  };
};
const selectFootnoteHeaderContentStyle = _ref2 => {
  let {
    footnoteHeaderPaddingTop,
    footnoteHeaderPaddingBottom,
    footnoteHeaderPaddingRight,
    footnoteHeaderPaddingLeft,
    headerMargin
  } = _ref2;
  return {
    paddingTop: footnoteHeaderPaddingTop,
    paddingBottom: footnoteHeaderPaddingBottom,
    paddingRight: footnoteHeaderPaddingRight,
    paddingLeft: footnoteHeaderPaddingLeft,
    marginRight: headerMargin
  };
};
const selectFootnoteCloseButtonStyle = _ref3 => {
  let {
    closeButtonBorderSize,
    closeButtonBorderColor,
    closeButtonHeight,
    closeButtonBackgroundColor,
    closeButtonMarginTop,
    closeButtonMarginRight,
    closeButtonMarginBottom,
    closeButtonMarginLeft,
    closeButtonWidth
  } = _ref3;
  return {
    backgroundColor: closeButtonBackgroundColor,
    borderWidth: closeButtonBorderSize,
    borderColor: closeButtonBorderColor,
    height: closeButtonHeight,
    marginTop: closeButtonMarginTop,
    marginRight: closeButtonMarginRight,
    marginBottom: closeButtonMarginBottom,
    marginLeft: closeButtonMarginLeft,
    width: closeButtonWidth
  };
};
const selectFootnoteBodyStyle = _ref4 => {
  let {
    maxWidth,
    footnoteBodyBackground,
    footnoteBodyPaddingTop,
    footnoteBodyPaddingRight,
    footnoteBodyPaddingBottom,
    footnoteBodyPaddingLeft,
    screenWidth
  } = _ref4;
  return {
    maxWidth,
    backgroundColor: footnoteBodyBackground,
    paddingTop: footnoteBodyPaddingTop,
    paddingRight: footnoteBodyPaddingRight * 2,
    paddingBottom: footnoteBodyPaddingBottom,
    paddingLeft: footnoteBodyPaddingLeft,
    width: screenWidth
  };
};
const selectCustomContentFontStyle = _ref5 => {
  let {
    listItemColor,
    listItemFontSize,
    listItemLineHeight,
    listItemPaddingLeft,
    listItemFontName,
    listItemFontWeight
  } = _ref5;
  return {
    fontSize: listItemFontSize,
    lineHeight: listItemLineHeight * listItemFontSize,
    paddingLeft: listItemPaddingLeft,
    color: listItemColor,
    fontName: listItemFontName,
    fontWeight: listItemFontWeight
  };
};

/**
 * Use `Footnote` to display a single legal content.
 *
 * ## Usage Criteria
 *
 * - Use `Footnote` to display a single legal statement
 * - Display on top of all UI, including other sticky elements such as Cart Summary
 * - Dismiss by clicking on the close button, clicking anywhere outside of the `Footnote`
 * - Use copy to set language, ‘en’ for English or ‘fr’ for French
 *
 * ## Accessibility requirements
 *
 * - Only one instance of `Footnote` should display at a time
 * - Place `Footnote` as the last element in the body or main
 * - When `Footnote` is open, the inert prop must be set on all children of body excluding the Footnote
 * - When `Footnote` is closed, focus must return to the initiating element
 */
const Footnote = /*#__PURE__*/_react.default.forwardRef((_ref6, ref) => {
  let {
    copy = 'en',
    number = undefined,
    content = undefined,
    onClose,
    isOpen = false,
    tokens,
    variant = {},
    dictionary = _dictionary.default,
    ...rest
  } = _ref6;
  const viewport = (0, _ViewportProvider.useViewport)();
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('Footnote', tokens, variant, {
    viewport
  });
  const themeOptions = (0, _ThemeProvider.useTheme)();
  const getCopy = (0, _utils.useCopy)({
    dictionary,
    copy
  });
  const [isVisible, setIsVisible] = _react.default.useState(false);
  const screenHeight = _Dimensions.default.get('screen').height;
  const screenWidth = _Dimensions.default.get('screen').width;
  const getFootnoteBodyContent = _react.default.useCallback(() => {
    if (!number || !content) {
      return null;
    }
    if (/*#__PURE__*/_react.default.isValidElement(content)) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: selectCustomContentFontStyle(themeTokens),
        children: content
      });
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_OrderedList.default, {
      start: number,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_OrderedList.default.Item, {
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
          style: selectCustomContentFontStyle(themeTokens),
          children: content
        })
      }, number)
    });
  }, [content, number, themeTokens]);
  _react.default.useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);
  const closeFootnote = _react.default.useCallback((event, options) => {
    onClose(event, options);
    setIsVisible(false);
  }, [onClose]);
  const handleClose = event => closeFootnote(event, {
    returnFocus: true
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    ...selectProps(rest),
    ref: ref,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Modal.default, {
      visible: isVisible,
      animationType: "slide",
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SafeAreaView.default, {
        style: staticStyles.container,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_ScrollView.default, {
          style: selectFootnoteStyle({
            screenHeight,
            isVisible,
            ...themeTokens
          }),
          children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
            style: staticStyles.content,
            children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
              style: [selectFootnoteHeaderContentStyle(themeTokens), staticStyles.headerContent],
              children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
                tokens: {
                  fontSize: themeTokens?.headerFontSize,
                  lineHeight: themeTokens?.headerLineHeight
                },
                variant: {
                  size: 'h4'
                },
                children: getCopy('heading')
              }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_TouchableWithoutFeedback.default, {
                onPress: handleClose,
                accessibilityLabel: getCopy('close'),
                children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
                  style: [selectFootnoteCloseButtonStyle(themeTokens), staticStyles.closeButton],
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
                    icon: themeTokens?.closeIcon,
                    tokens: {
                      size: themeTokens?.closeButtonIconSize
                    }
                  })
                })
              })]
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
              style: selectFootnoteBodyStyle({
                maxWidth: themeOptions.contentMaxWidth,
                screenWidth,
                ...themeTokens
              }),
              children: getFootnoteBodyContent()
            })]
          })
        })
      })
    })
  });
});
Footnote.displayName = 'Footnote';
const copyShape = _propTypes.default.shape({
  close: _propTypes.default.string.isRequired,
  heading: _propTypes.default.string.isRequired
});

// If a language dictionary entry is provided, it must contain every key
const dictionaryContentShape = _propTypes.default.shape({
  a11yLabel: _propTypes.default.string.isRequired,
  close: _propTypes.default.string.isRequired,
  heading: _propTypes.default.string.isRequired
});
Footnote.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('Footnote'),
  /**
   * The content.
   */
  content: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Use the `copy` prop to either select provided English or French copy by passing 'en' or 'fr' respectively.
   * To provide your own, pass a JSON object with the keys `heading` and `close`.
   */
  copy: _propTypes.default.oneOfType([_propTypes.default.oneOf(['en', 'fr']), copyShape]),
  /**
   * A boolean flag used hide or show the `Footnote`. Set to `true` to open the `Footnote`.
   */
  isOpen: _propTypes.default.bool,
  /**
   * The number, must match the number of the `FootnoteLink` that initiated the `Footnote`.
   */
  number: _propTypes.default.number,
  /**
   * A callback function to handle the closing of the footnote.
   *
   * @param {SyntheticEvent} event The React `SyntheticEvent`
   * @param {Object} options Custom options
   * @param {boolean} options.returnFocus Should the `Footnote` return focus on close
   */
  onClose: _propTypes.default.func.isRequired,
  /**
   * Override the default dictionary, by passing the complete dictionary object for `en` and `fr`
   */
  dictionary: _propTypes.default.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  })
};
var _default = exports.default = Footnote;
const staticStyles = _StyleSheet.default.create({
  container: {
    flex: 1
  },
  content: {
    marginLeft: 'auto',
    marginRight: 'auto',
    left: 0,
    right: 0,
    maxWidth: 1200,
    paddingBottom: 100
  },
  headerContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 50,
    display: 'flex',
    justifyContent: 'center'
  }
});